/**
 * Parse a free-text AU address blob into address / suburb / state / postcode.
 *
 * A seller lead carries one free-text address (Lead.property_address); the Add
 * Appraisal form makes Suburb, State and Postcode their own required fields, so
 * the blob is split here to carry them over instead of the agent retyping them
 * (CRM-2026-00042, and CG #12985966294: "Please also fill in the suburb &
 * State"). Best-effort: leaves a field blank rather than guessing wrong.
 *
 * Mirrors the backend Database-convert helper
 * prospecting/services/convert.py::_state_and_postcode.
 */
const AU_STATE_RE = /\b(VIC|NSW|QLD|SA|WA|TAS|NT|ACT)\b/i;
const AU_POSTCODE_RE = /\b(\d{4})\s*$/; // anchored to the tail: a leading 4-digit run is a street number, not a postcode
// Common AU street-type suffixes, used to find where the street ends and the
// suburb begins when the address has no comma ("12 Smith Street Heidelberg West"
// -> street "12 Smith Street", suburb "Heidelberg West"). Kept broad so it
// catches the abbreviations agents actually type.
const AU_STREET_TYPE_RE = new RegExp(
  "\\b(st|street|rd|road|ave|av|avenue|ct|court|pde|parade|dr|drive|cres|crescent|" +
  "pl|place|ln|lane|way|blvd|boulevard|tce|terrace|hwy|highway|cl|close|gr|grove|" +
  "sq|square|cct|circuit|esp|esplanade|row|mews|rise|walk|wynd|gdns|gardens|" +
  "cir|circle|hts|heights|prom|promenade|qy|quay|strip|track|trk)\\b\\.?",
  "i",
);

// AU postcode -> state, by the official ranges, so State fills in even when the
// address text names no state. ACT ranges are checked before the NSW 2xxx range.
export const stateFromPostcode = (pc) => {
  const n = parseInt(pc, 10);
  if (!Number.isFinite(n)) return "";
  if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return "ACT";
  if ((n >= 1000 && n <= 2599) || (n >= 2619 && n <= 2899) || (n >= 2921 && n <= 2999)) return "NSW";
  if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return "VIC";
  if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return "QLD";
  if (n >= 5000 && n <= 5999) return "SA";
  if (n >= 6000 && n <= 6999) return "WA";
  if (n >= 7000 && n <= 7999) return "TAS";
  if (n >= 800 && n <= 999) return "NT";
  return "";
};

export const parseAuAddress = (raw) => {
  const full = String(raw || "").trim();
  const out = { address: full, suburb: "", state: "", postal_code: "" };
  if (!full) return out;

  const stateM = full.match(AU_STATE_RE);
  const pcM = full.match(AU_POSTCODE_RE);
  out.postal_code = pcM ? pcM[1] : "";
  // Prefer the state named in the text; otherwise derive it from the postcode.
  out.state = stateM ? stateM[1].toUpperCase() : stateFromPostcode(out.postal_code);

  // Strip the state + trailing postcode off the tail so what remains is
  // "<street> <suburb>" (with or without a comma between them).
  let tail = full;
  if (out.postal_code) tail = tail.replace(AU_POSTCODE_RE, "").trim();
  if (stateM) tail = tail.replace(AU_STATE_RE, "").trim();
  tail = tail.replace(/,\s*$/, "").trim();

  const parts = tail.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    // Comma form: the suburb is the last comma-separated segment.
    out.suburb = parts[parts.length - 1];
    out.address = parts.slice(0, -1).join(", ");
  } else if (parts.length === 1) {
    // No comma: split on the LAST street-type token, so a two-word suburb
    // ("Heidelberg West", "Fitzroy North") is kept whole. If there's no
    // recognisable street type, leave the suburb blank rather than guess.
    const single = parts[0];
    let lastMatch = null;
    const re = new RegExp(AU_STREET_TYPE_RE.source, "ig");
    let m;
    while ((m = re.exec(single)) !== null) lastMatch = m;
    if (lastMatch) {
      const end = lastMatch.index + lastMatch[0].length;
      const street = single.slice(0, end).trim();
      const suburb = single.slice(end).replace(/^[,\s]+/, "").trim();
      if (suburb) {
        out.address = street;
        out.suburb = suburb;
      }
    }
  }
  return out;
};
